import React, { useMemo } from "react";
import { useParams, useRouteMatch } from "react-router-dom";
import { useAccountFinancials } from "../hooks/useAccountFinancials";
import { FinancialDetail } from "./FinancialDetail";
import { Typography, BoxProps } from "@material-ui/core";
import { formatMoneyWithSign } from "../../../utils/formatMoney";

export const AccountBalance: React.FC<BoxProps> = ({ ...props }) => {
  const { accountNumber } = useParams();
  const { data, status } = useAccountFinancials(accountNumber!);
  const { balance, overdueBalance, currency } = data?.balance || {};
  const { method } = data?.payment || {};
  const { url } = useRouteMatch();

  const {
    formattedBalance,
    formattedOverdueBalance,
    isCredit,
    isApplicable,
  } = useMemo(() => {
    if (currency === null) return { isApplicable: false }; //Display nothing for unbilled accounts

    const formattedBalance =
      balance && currency
        ? formatMoneyWithSign(currency!, balance, {
            transform: Math.abs,
          })
        : null;
    const formattedOverdueBalance =
      overdueBalance && currency && Number(overdueBalance) !== 0
        ? formatMoneyWithSign(currency!, overdueBalance, {
            transform: Math.abs,
          })
        : null;
    const isApplicable = method
      ? method === "DirectDebit" || method === "Other"
      : method; //Return undefined during loading state
    return {
      formattedBalance,
      formattedOverdueBalance,
      isApplicable,
      isCredit: balance ? Number(balance) < 0 : null,
    };
  }, [balance, overdueBalance, currency, method]);

  if (isApplicable === false) return null;

  return (
    <FinancialDetail
      loading={status === "loading"}
      title="Account balance"
      link={{ text: "View Financials", path: `${url}/financials` }}
      subtitles={
        formattedOverdueBalance
          ? [formattedOverdueBalance + " overdue"]
          : undefined
      }
      {...props}
    >
      <Typography variant="h5" color="textPrimary" component="span">
        {formattedBalance}
      </Typography>
      {typeof isCredit === "boolean" && (
        <Typography variant="body1" color="textSecondary" component="span">
          {isCredit ? " CR" : " DR"}
        </Typography>
      )}
    </FinancialDetail>
  );
};
